/**
 * RequireExplainer — explainer for the REQUIRE Structural Duality Principle (At).
 *
 * REQUIRE is the level-2 SDP that pairs with PRECOMMIT along the
 * monad ⊣ comonad adjunction. It certifies that the demand-side cannot
 * launder the production-side witness discipline — a PRECOMMIT witness
 * can be stacked through arbitrary refactorings without producing a
 * forbidden pattern. With PRECOMMIT (primal) it closes as the ICG-At
 * Safety Envelope.
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */

import React from "react";
import {
  CalloutBox,
  ExplainerCard,
  GradedAdjunctionDiagram,
  SdpLevelBadge,
  type ExplainerCrossLink,
  type ExplainerIdentity,
  type ExplainerTierContent,
} from "../shared/index.js";
import { COMPOSITIONALITY_PRINCIPLE_COLOR, THEOREM_COLOR } from "../../design-system/tokens.js";

const ACCENT = COMPOSITIONALITY_PRINCIPLE_COLOR.REQUIRE.color;
const LEVEL = COMPOSITIONALITY_PRINCIPLE_COLOR.REQUIRE.level;

const IDENTITY: ExplainerIdentity = {
  id: "explainer-require",
  kind: `SSM · Compositionality Principle (S2 · level ${LEVEL})`,
  title: "REQUIRE",
  subtitle: "At · monad ↔ comonad · Neutralization Theorem: W",
  accent: ACCENT,
  accentAlt: THEOREM_COLOR.PRECOMMIT,
};

const CROSS_LINKS: readonly ExplainerCrossLink[] = [
  {
    label: "PRECOMMIT (W, primal Neutralization Theorem)",
    href: "#explainer-precommit",
    accent: THEOREM_COLOR.PRECOMMIT,
  },
  {
    label: "ICG-At (Safety Envelope)",
    href: "#explainer-icg-at",
    accent: THEOREM_COLOR.PRECOMMIT,
  },
  { label: "Monad", href: "#explainer-monad" },
  { label: "Comonad", href: "#explainer-comonad" },
  { label: "Graded monad", href: "#explainer-graded-monad" },
];

const TIERS: ExplainerTierContent = {
  tldr: (
    <p>
      <strong>REQUIRE</strong> is the safety story for stacking commitments: it certifies that "an
      irreversible action carries its precommitment" composes safely — by the monad ↔ comonad
      duality, not by inspection.
    </p>
  ),
  intuition: (
    <>
      <p>
        Once you can write "this effect committed before it ran" (a PRECOMMIT witness), you
        immediately want to stack two of them. REQUIRE is the certificate that says stacking is safe
        — the demand side that <em>requires</em> a commitment cannot launder the production side
        that <em>tracks</em> it.
      </p>
      <p>
        Where PRECOMMIT delivers the guarantee inside one regime, REQUIRE delivers compositionality
        across regimes. Together they close as the ICG-At Safety Envelope.
      </p>
    </>
  ),
  formal: (
    <>
      <p>
        REQUIRE is the level-2 Structural Duality Principle that pairs the commitment-tracking monad
        with the requirement-discharging comonad as a <strong>graded</strong> adjunction.
      </p>
      <p style={{ marginTop: "0.5rem" }}>
        <strong>1-categorical seam vs SDP graded adjunction</strong> (per{" "}
        <code>theory/structural/SEAM-ADJUNCTION-BRIDGE-KAN.md</code>). At the 1-categorical level
        the PRECOMMIT seam declares an <em>absolute empty hom-set</em>: the unwitnessed irreversible
        morphism either exists or it does not, in the strict binary sense. As a{" "}
        <strong>level-2</strong> SDP, REQUIRE operates through a 2-categorical <em>graded</em>{" "}
        adjunction: a ladder of grades whose unit/counit supplies, at each grade, the precise
        commitment a bridge must produce — structurally inexpressible without the witness,
        expressible with it. Level-2 SDPs come from higher-categorical adjunctions for singleton
        Neutralization Theorems (here, W alone).
      </p>
    </>
  ),
  relevance: (
    <>
      <p>
        REQUIRE is what lets the ICG-At Safety Envelope reduce the audit surface from "a PRECOMMIT
        witness per regime" to "one envelope" carrying the graded adjunction unit and counit.
      </p>
      <p>
        <strong>Theorem-collision check.</strong> If a demand-side requirement stacks but the joint
        is not a PRECOMMIT witness, REQUIRE rules it out structurally — no graded-adjunction unit
        certifies the stack. The forbidden composition is <em>structurally inexpressible</em> at the
        SDP level.
      </p>
      <CalloutBox eyebrow="Safety envelope: ICG-At" accent={ACCENT}>
        PRECOMMIT (Neutralization Theorem) ⊗ REQUIRE (SDP) = ICG-At, the single two-sided
        certificate. Audit surface collapses from{" "}
        <em>(Neutralization Theorem witness) + (SDP witness)</em> to <em>one envelope</em> carrying
        the monad ⊣ comonad unit and counit.
      </CalloutBox>
    </>
  ),
  references: [
    {
      label: "Theorem Canon — SDPs and ICGs (§7, §7a)",
      path: "docs/reference/Theorem-Canon.md",
    },
    {
      label: "Seam adjunction / bridge Kan theory",
      path: "theory/structural/SEAM-ADJUNCTION-BRIDGE-KAN.md",
      note: "Authoritative treatment of the empty-hom-set seam vs the graded SDP adjunction; AGENTS.md routes here.",
    },
    {
      label: "PRECOMMIT kernel module",
      path: "agda/Kernel/PRECOMMIT/Neutralization.agda",
    },
  ],
};

const DIAGRAM = (
  <>
    <div style={{ marginBottom: "0.5rem" }}>
      <SdpLevelBadge level={LEVEL} accent={ACCENT} />
    </div>
    <GradedAdjunctionDiagram
      accent={ACCENT}
      grade="k"
      ariaLabel="REQUIRE graded adjunction: commitment-tracking monad ⊣ requirement-discharging comonad, indexed by effect grade k"
    />
  </>
);

export function RequireExplainer(): React.JSX.Element {
  return (
    <ExplainerCard identity={IDENTITY} tiers={TIERS} diagram={DIAGRAM} crossLinks={CROSS_LINKS} />
  );
}
